// ============================================================================
// CLEO'S SECRET — ApproachPage
// File: src/pages/ApproachPage.jsx
//
// Reconciled to docs/approach-page-copy.md (locked May 2026). Section arc:
//   1. Hook (SAND) — premise approach + Cleopatra subtitle (the ONE
//      Cleopatra invocation on this page, doc rule)
//   2. GP-led clinical model (OBS) — "led by GPs" phrasing locked, no
//      "Every patient is seen by a GP" (banned), 4-stat colonnade
//   3. Three pillars (SAND) — Knowledge / Sovereignty / Ritual cards
//      without Cleopatra-style cleoLines (doc has no such field;
//      "she ruled" is banned platform-wide)
//   4. The Clinical Pathway (PAP papyrus inset) — 4 structured steps
//      from doc Section 6, TextLinkCta closure routes to /membership
//   5. AI / Cleo intake (OBS)
//   6. Invitation (OBS) — InvitationBlock atom replacing the retired
//      "Founding principle" close
//
// Em-dash conversions from doc:
//   • Hook subtitle: "the same three disciplines — Knowledge, Sovereignty,
//     Ritual — shape" → "the same three disciplines, Knowledge, Sovereignty,
//     Ritual, shape" (commas)
//   • Section 2 subtitle: "midlife health — with" → "midlife health, with"
//   • Section 2 body: "actually asking — and the time" → "actually asking,
//     and the time"
//   • Pillar I body: "language you can use — every marker" → comma
//   • Pillar III body: "the structure — and the structure" → comma
//   • Clinical Pathway subtitle: "considered sequence — from first" → comma
//   • Clinical Pathway step 3: "twelve weeks — foundations first" → comma
// ============================================================================

import React from 'react';
import {
  FF, P, T,
  SECTION_PADDING, CONTAINER_MAX, CONTAINER_READING,
  Eyebrow, SectionDivider, SectionHeadline,
  Colonnade, BtnPrimary,
} from '../lib/design-tokens';
import InvitationBlock from '../components/marketing/InvitationBlock';

// ───────── Pillar card — used in Section 3.
//   cleoLine field removed per Session 4 doc reconciliation: doc has no
//   such field, and the previous Cleopatra-style lines violated both the
//   "Cleopatra invoked once in hook only" rule and the platform-wide ban
//   on "she ruled" phrasing.
function Pillar({ numeral, title, promise, body }) {
  return (
    <div style={{
      padding: 'clamp(40px, 4.5vw, 64px) clamp(28px, 3.2vw, 48px) clamp(40px, 4.2vw, 60px)',
      background: 'rgba(250, 246, 238, 0.55)',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    }}>
      <div style={{ ...T.numeral, color: P.GO, opacity: 0.9, marginBottom: 14 }}>{numeral}</div>
      <div style={{
        width: 'clamp(40px, 4vw, 56px)',
        height: 1,
        background: P.GO,
        opacity: 0.65,
        marginBottom: 24,
      }} />
      <h3 style={{ ...T.label, color: P.BR2, marginBottom: 18 }}>{title}</h3>
      <p style={{
        ...T.bodyMd,
        color: P.BR2,
        fontStyle: 'italic',
        marginBottom: 22,
        maxWidth: 280,
      }}>{promise}</p>
      <p style={{
        ...T.bodyLg,
        fontSize: 'clamp(16px, 1.2vw, 18px)',
        color: P.TX,
        textAlign: 'justify',
        hyphens: 'auto',
        maxWidth: 340,
      }}>{body}</p>
    </div>
  );
}

// ───────── Stat tile — used in Section 2 clinical-frame stats strip
function Stat({ value, label }) {
  return (
    <div style={{
      padding: 'clamp(36px, 4vw, 56px) clamp(20px, 2.5vw, 36px)',
      textAlign: 'center',
    }}>
      <div style={{ ...T.statNum, color: P.GO2, marginBottom: 12 }}>{value}</div>
      <div style={{
        ...T.label,
        color: 'rgba(240,232,212,0.62)',
        fontSize: 'clamp(11px, 0.9vw, 13px)',
        letterSpacing: '0.3em',
      }}>{label}</div>
    </div>
  );
}

// ───────── Pathway step — used in Section 4 Clinical Pathway
function PathwayStep({ numeral, label, body }) {
  return (
    <div style={{
      padding: 'clamp(36px, 4vw, 56px) clamp(20px, 2.5vw, 32px)',
      textAlign: 'center',
    }}>
      <div style={{
        ...T.numeral,
        fontSize: 'clamp(36px, 4.4vw, 64px)',
        color: P.GO,
        opacity: 0.85,
        marginBottom: 14,
      }}>{numeral}</div>
      <div style={{
        width: 'clamp(36px, 3.4vw, 48px)',
        height: 1,
        background: P.GO,
        opacity: 0.55,
        margin: '0 auto 22px',
      }} aria-hidden="true" />
      <h4 style={{
        fontFamily: FF,
        fontSize: 'clamp(15px, 1.4vw, 18px)',
        fontStyle: 'italic',
        fontWeight: 500,
        color: P.BR2,
        marginBottom: 16,
        letterSpacing: '-0.005em',
        lineHeight: 1.3,
      }}>{label}</h4>
      <p style={{
        ...T.bodyMd,
        color: P.TX,
        maxWidth: 280,
        margin: '0 auto',
      }}>{body}</p>
    </div>
  );
}

// ───────── TextLinkCta — italic Cormorant hover-underline text-link
//   Inline-copy of LandingPage's local atom. Extraction to a shared
//   marketing atom deferred until a 3rd consumer exists.
function TextLinkCta({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontFamily: FF,
        fontSize: 'clamp(16px, 1.5vw, 19px)',
        fontStyle: 'italic',
        fontWeight: 500,
        color: P.BR2,
        background: 'transparent',
        border: 'none',
        borderBottom: '1px solid rgba(90, 63, 24, 0.35)',
        padding: '4px 0',
        cursor: 'pointer',
        transition: 'border-color 0.2s, color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = P.BR2;
        e.currentTarget.style.color = P.INK;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'rgba(90, 63, 24, 0.35)';
        e.currentTarget.style.color = P.BR2;
      }}
    >{children}</button>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ApproachPage
// ════════════════════════════════════════════════════════════════════════════
export default function ApproachPage({ onNavigate }) {
  var goIntake = function() { if (onNavigate) onNavigate('intake'); };

  return (
    <div style={{ background: P.PAP, color: P.INK, fontFamily: FF }}>

      {/* ═════════ SECTION 1 — Hook (cream / SAND) ═════════
          Hook eyebrow + H2 kept as-is per Session 4 minimum scope. Body
          replaced with doc-locked subtitle treatment (italic gold centred).
          This is the ONE Cleopatra invocation on the page. */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <Eyebrow center>Our approach</Eyebrow>
          <div style={{ marginTop: 'clamp(24px, 2.5vw, 36px)' }}>
            <SectionHeadline
              roman="She never accepted normal"
              italic="as an answer."
            />
          </div>
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <p style={{
              ...T.bodyLg,
              fontStyle: 'italic',
              color: P.BR2,
              textAlign: 'center',
            }}>
              Cleopatra studied her body. She made her own decisions. She invested in herself deliberately. Two thousand years later, the same three disciplines, Knowledge, Sovereignty, Ritual, shape how we practise medicine.
            </p>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 2 — GP-led clinical model (dark / OBS) ═════════
          Body swapped to doc-locked text. Banned phrase "Every patient is
          seen by a GP" replaced with doc-locked "led by GPs" phrasing in
          the new italic subtitle. */}
      <section style={{ background: P.OBS, padding: SECTION_PADDING, color: P.IVY }}>
        <div style={{ maxWidth: CONTAINER_MAX, margin: '0 auto' }}>
          <SectionDivider onDark />
          <div style={{ marginTop: 48, marginBottom: 28, textAlign: 'center' }}>
            <Eyebrow center color={P.GO2}>GP-led clinical model</Eyebrow>
          </div>
          <SectionHeadline
            roman="This is not wellness."
            italic="This is medicine."
            onDark
          />
          <div style={{ maxWidth: 820, margin: '0 auto 28px' }}>
            <p style={{
              ...T.bodyLg,
              fontStyle: 'italic',
              color: P.GO2,
              textAlign: 'center',
            }}>
              Clinical care led by GPs who specialise in perimenopause, menopause, and women&rsquo;s midlife health, with the diagnostic depth and continuity this season asks for.
            </p>
          </div>
          <div style={{ maxWidth: 820, margin: '0 auto clamp(48px, 5vw, 80px)' }}>
            <p style={{
              ...T.bodyLg,
              color: 'rgba(240,232,212,0.84)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              Not a wellness coach. Not an algorithm. A clinical model built for the questions women in perimenopause and menopause are actually asking, and the time those questions deserve.
            </p>
          </div>

          {/* Stats colonnade — 4-stat strip per doc */}
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>
            <Colonnade columns={4} ruleColor={P.GO2} ruleOpacity={0.4} diamondColor={P.GO2} diamondSize={10}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                padding: 'clamp(20px, 2.5vw, 40px) 0',
              }}>
                <Stat value="45 min"   label="Initial consultation" />
                <Stat value="60+"      label="Biomarkers tested" />
                <Stat value="12 pages" label="Clinical report" />
                <Stat value="12 weeks" label="Structured program" />
              </div>
            </Colonnade>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 3 — Three pillars (cream / SAND) ═════════
          Pillar opener H2 reconciled: was "Built on the wisdom of Cleopatra"
          (Cleopatra-beyond-hook violation), now "Three disciplines, / one
          clinical model." Opener body reconciled: was Cleopatra-ruled
          violation, now brief editorial single line. Pillar bodies swapped
          to doc-locked text; cleoLine field removed from Pillar component;
          taglines updated to doc verbatim. */}
      <section style={{ background: P.SAND, padding: SECTION_PADDING }}>
        <div style={{ maxWidth: CONTAINER_MAX, margin: '0 auto' }}>
          <SectionDivider />
          <div style={{ marginTop: 48, marginBottom: 28, textAlign: 'center' }}>
            <Eyebrow center>The three pillars</Eyebrow>
          </div>
          <SectionHeadline
            roman="Three disciplines,"
            italic="one clinical model."
          />
          <div style={{ maxWidth: 760, margin: '0 auto clamp(56px, 5.5vw, 88px)' }}>
            <p style={{
              ...T.bodyLg,
              fontStyle: 'italic',
              color: P.BR2,
              textAlign: 'center',
            }}>
              Three disciplines, woven through every part of the clinical model.
            </p>
          </div>

          {/* Pillars colonnade */}
          <div style={{ maxWidth: 1480, margin: '0 auto' }}>
            <Colonnade columns={3} ruleColor={P.GO} ruleOpacity={0.55} diamondSize={14}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              }}>
                <Pillar
                  numeral="I"
                  title="Knowledge"
                  promise="Clinical depth, not vague reassurance."
                  body="Sixty-plus biomarkers, read against your story rather than a one-size-fits-all reference range. Each result is then explained in a twelve-page clinical report, in language you can use, every marker tied to your symptoms, your function, and the decisions ahead of you."
                />
                <Pillar
                  numeral="II"
                  title="Sovereignty"
                  promise="Your body, your authority."
                  body="We inform. You decide. Your GP brings the clinical depth, contextualises your results, and lays out the options honestly. The decision, every protocol, every next step, belongs to you. From a place of knowledge, not anxiety or pressure."
                />
                <Pillar
                  numeral="III"
                  title="Ritual"
                  promise="Considered care, repeated deliberately."
                  body="Lifestyle foundations first. Medical support where clinically indicated. GP oversight throughout. Then quarterly reviews to recalibrate as you do. The depth comes from the structure, and the structure earns its name when it&rsquo;s done properly the first time."
                />
              </div>
            </Colonnade>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 4 — The Clinical Pathway (papyrus / PAP inset) ═════════
          Net-new section per doc Section 6. 4 structured steps from first
          audit to ongoing membership. PAP papyrus inset bg breaks the
          SAND-OBS rhythm with a structured-detail interlude. */}
      <section style={{ background: P.PAP, padding: SECTION_PADDING }}>
        <div style={{ maxWidth: CONTAINER_MAX, margin: '0 auto' }}>
          <SectionDivider />
          <div style={{ marginTop: 48, marginBottom: 28, textAlign: 'center' }}>
            <Eyebrow center>The clinical pathway</Eyebrow>
          </div>
          <SectionHeadline
            roman="Four structured steps,"
            italic="toward informed decisions."
          />
          <div style={{ maxWidth: 800, margin: '0 auto clamp(56px, 5.5vw, 80px)' }}>
            <p style={{
              ...T.bodyLg,
              fontStyle: 'italic',
              color: P.BR2,
              textAlign: 'center',
            }}>
              Every client moves through the same considered sequence, from first audit to ongoing clinical partnership.
            </p>
          </div>

          {/* 4-step pathway in Colonnade — matches existing visual language */}
          <div style={{ maxWidth: 1480, margin: '0 auto' }}>
            <Colonnade columns={4} ruleColor={P.GO} ruleOpacity={0.55} diamondSize={12}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              }}>
                <PathwayStep
                  numeral="01"
                  label="Performance audit · 12 min · free"
                  body="Four scored dimensions. A clinical profile summary. Yours to keep."
                />
                <PathwayStep
                  numeral="02"
                  label="Diagnostic · 90 min"
                  body="Intake nurse pre-consult, GP telehealth, comprehensive pathology, and your twelve-page clinical report. Available as a one-off, or as the entry into membership."
                />
                <PathwayStep
                  numeral="03"
                  label="Twelve-week program"
                  body="Structured clinical care over twelve weeks, foundations first, medical support where indicated, GP oversight throughout."
                />
                <PathwayStep
                  numeral="04"
                  label="Ongoing membership"
                  body="Quarterly GP reviews and ongoing pathology, recalibrating as you do."
                />
              </div>
            </Colonnade>
          </div>

          {/* See membership options → text-link CTA per doc */}
          <div style={{ textAlign: 'center', marginTop: 'clamp(56px, 6vw, 88px)' }}>
            <TextLinkCta onClick={() => onNavigate('membership')}>
              See membership options &rarr;
            </TextLinkCta>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 5 — AI / Cleo intake (dark / OBS) ═════════ */}
      <section style={{ background: P.OBS, padding: SECTION_PADDING, color: P.IVY }}>
        <div style={{ maxWidth: CONTAINER_READING, margin: '0 auto', textAlign: 'center' }}>
          <SectionDivider onDark />
          <div style={{ marginTop: 48, marginBottom: 28 }}>
            <Eyebrow center color={P.GO2}>Technology in service of medicine</Eyebrow>
          </div>
          <SectionHeadline
            roman="AI that gives your GP"
            italic="more time with you."
            onDark
          />
          <div style={{ maxWidth: 760, margin: '0 auto clamp(40px, 4vw, 64px)' }}>
            <p style={{
              ...T.bodyLg,
              color: 'rgba(240,232,212,0.84)',
              textAlign: 'justify',
              hyphens: 'auto',
            }}>
              Before your first appointment, our intake specialist Cleo gets to know you: your symptoms, your history, your goals. By the time you sit with your GP, they already understand the shape of your story. The consultation begins where it should, with the clinical conversation that matters.
            </p>
          </div>
          <div>
            <BtnPrimary onClick={goIntake} large onDark>Speak with Cleo</BtnPrimary>
          </div>
        </div>
      </section>

      {/* ═════════ SECTION 6 — Invitation (dark / OBS) ═════════
          Replaces the retired "Founding principle" close per doc. Same
          doorway as every other page. */}
      <section style={{ background: P.OBS, padding: SECTION_PADDING, color: P.IVY }}>
        <InvitationBlock onDark onCtaClick={goIntake} />
      </section>

    </div>
  );
}
